import React, { useMemo } from "react";
import { Box, Button, Chip, Grid2, Stack, Typography, useTheme } from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import { useNavigate } from "react-router-dom";

import { useQuery, useMutation } from "@apollo/client";
import { FETCH_SUBSCRIPTION_INFO } from "@company/graphql/queries/fetchSubscriptionInfo";
import { useSubscriptionAccess } from "@company/hooks/useSubscriptionAccess";
import { useServerTime } from "@company/hooks/useServerTime";

import BaseSettingsPage from "../../base/BaseSettingsPage";
import ServiceCreditsDisplay from "@settings/components/ServiceCreditsDisplay";
import SubscriptionOverviewCard from "../../components/cards/SubscriptionOverviewCard";
import { calculateSeatOccupancy } from "@/utils/seatUtils";
import SeatCountDisplay from "@settings/components/SeatCountDisplay";
import { UserSize, User } from "@/app/graphql/codegen";
import { FETCH_COMPANY } from "@company/graphql/queries/fetchCompany";
import TrialStatusCard from "../../components/cards/TrialStatusCard";
import { BILLING_PORTAL_URL } from "@shop/graphql/mutations/billingPortalUrl";

// Erweiterte Typen für Nutzer und Einladungslinks mit size
interface ExtendedUser extends User {
    size: UserSize;
}

interface ExtendedInviteLink {
    id: string;
    isUsed: boolean;
    size: UserSize;
}

const SubscriptionOverviewPage = () => {
    const theme = useTheme();
    
    // Use enhanced subscription access hook - NEW
    const subscriptionAccess = useSubscriptionAccess();
    
    // Get server time for accurate trial calculations
    const { serverTime } = useServerTime();
    
    // Fetch subscription info for detailed information
    const { data, loading } = useQuery(FETCH_SUBSCRIPTION_INFO, {
        errorPolicy: "ignore",
    });

    const subscriptionInfo = data?.subscriptionInfo;

    // Billing portal mutation
    const [getBillingPortalUrl, { loading: portalLoading }] = useMutation(BILLING_PORTAL_URL);

    // Company data for seat calculation
    const { data: companyData } = useQuery(FETCH_COMPANY);
    const companyName = companyData?.company?.name;

    // Formatiere das Enddatum
    const formattedEndDate = subscriptionInfo?.currentPeriodEnd
        ? new Date(subscriptionInfo.currentPeriodEnd).toLocaleDateString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
          })
        : "Unbekannt";

    // Formatiere nächste Kontingent-Aktualisierung
    const formattedNextRefresh = subscriptionInfo?.nextCreditRefreshAt
        ? new Date(subscriptionInfo.nextCreditRefreshAt).toLocaleDateString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
          })
        : "Unbekannt";

    // Berechne Sitzplatzbelegung - optimiert für Performance
    const seatOccupancy = useMemo(() => {
        if (!subscriptionInfo || !companyData?.company) {
            return {
                total: { occupied: 0, total: 0 },
                S: { occupied: 0, total: 0 },
                M: { occupied: 0, total: 0 },
                L: { occupied: 0, total: 0 },
            };
        }

        const subscription = {
            seatsS: subscriptionInfo.seatsS || 0,
            seatsM: subscriptionInfo.seatsM || 0,
            seatsL: subscriptionInfo.seatsL || 0,
        };

        // Filtere Benutzer ohne Größenangabe und wandle in ExtendedUser um
        const usersWithSize = (companyData.company.users || []).filter((user: any): user is ExtendedUser => user && user.size !== undefined && user.size !== null);

        // Filtere Einladungslinks ohne Größenangabe und wandle in ExtendedInviteLink um
        const inviteLinksWithSize = (companyData.company.inviteLinks || []).filter(
            (link: any): link is ExtendedInviteLink => link && link.size !== undefined && link.size !== null && link.isUsed !== undefined,
        );

        // Verwende die Hilfsfunktion zur Berechnung der Sitzplatzbelegung
        return calculateSeatOccupancy(usersWithSize, subscription, inviteLinksWithSize);
    }, [subscriptionInfo, companyData]);

    // Handler zum Öffnen des Stripe-Kundenportals
    const handleOpenBillingPortal = async () => {
        try {
            const { data } = await getBillingPortalUrl();
            if (data?.billingPortalUrl) {
                window.location.href = data.billingPortalUrl;
            }
        } catch (error) {
            console.error("Fehler beim Öffnen des Kundenportals:", error);
        }
    };

    // Use enhanced subscription access instead of direct status checking - UPDATED
    const hasActiveSubscription = subscriptionAccess.hasAccess;

    const navigate = useNavigate();

    // Handler for trial upgrade button
    const handleTrialUpgrade = () => {
        navigate("/shop");
    };

    return (
        // Titel mit Unternehmensname anzeigen
        <BaseSettingsPage title={`Abonnement${companyName ? ` – ${companyName}` : ""}`}>
            <Stack spacing={1.5}>
                {/* Trial Status Card - Show prominently at top if in trial */}
                {subscriptionInfo?.isInTrial && (
                    <TrialStatusCard
                        isInTrial={subscriptionInfo.isInTrial}
                        trialEnd={subscriptionInfo.trialEnd || null}
                        trialStarted={subscriptionInfo.trialStarted || null}
                        onUpgradeClick={handleTrialUpgrade}
                        serverTime={serverTime}
                    />
                )}

                {/* Enhanced Grace Period Warning - NEW */}
                {subscriptionAccess.isInGracePeriod && (
                    <SubscriptionOverviewCard title={"⚠️ Kulanzfrist aktiv"}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body1" color="warning.main" sx={{ mb: 1 }}>
                                Ihr Abonnement befindet sich in der Kulanzfrist.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Bitte aktualisieren Sie Ihre Zahlungsinformationen, um eine Unterbrechung des Service zu vermeiden.
                            </Typography>
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<PaymentIcon />}
                                onClick={handleOpenBillingPortal}
                                disabled={portalLoading}
                                sx={{ mt: 2 }}
                            >
                                Zahlungsinformationen aktualisieren
                            </Button>
                        </Box>
                    </SubscriptionOverviewCard>
                )}

                {/* Nutzeranzahl */}
                <SubscriptionOverviewCard title={"Anzahl Nutzer"}>
                    <Grid2 container spacing={3} sx={{ px: 1 }}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: "flex", justifyContent: "center" }}>
                            <SeatCountDisplay count={seatOccupancy.total.occupied} total={seatOccupancy.total.total} label="Gesamt" size="TOTAL" />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: "flex", justifyContent: "center" }}>
                            <SeatCountDisplay count={seatOccupancy.S.occupied} total={seatOccupancy.S.total} size={UserSize.S} />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: "flex", justifyContent: "center" }}>
                            <SeatCountDisplay count={seatOccupancy.M.occupied} total={seatOccupancy.M.total} size={UserSize.M} />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: "flex", justifyContent: "center" }}>
                            <SeatCountDisplay count={seatOccupancy.L.occupied} total={seatOccupancy.L.total} size={UserSize.L} />
                        </Grid2>
                    </Grid2>
                </SubscriptionOverviewCard>

                {/* Verfügbare Services */}
                <SubscriptionOverviewCard title={"Verfügbare Services"}>
                    {loading ? <ServiceCreditsDisplay serviceCredits={[]} isLoading={true} /> : <ServiceCreditsDisplay serviceCredits={subscriptionInfo?.serviceCredits || []} />}
                </SubscriptionOverviewCard>

                {/* Kündigung */}
                <SubscriptionOverviewCard title={"Vertragsinformationen"} /*action={{ label: "Kündigen", onClick: () => {} }}*/>
                    <Box sx={{ p: 2 }}>
                        {subscriptionInfo?.isInTrial ? (
                            <Box>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <Chip label="Testphase aktiv" color="primary" size="small" sx={{ mr: 1 }} />
                                    Kostenlose 7-Tage-Testversion
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Testphase endet:{" "}
                                    {subscriptionInfo.trialEnd
                                        ? new Date(subscriptionInfo.trialEnd).toLocaleDateString("de-DE", {
                                              day: "2-digit",
                                              month: "2-digit",
                                              year: "numeric",
                                          })
                                        : "Unbekannt"}
                                </Typography>
                                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                                    Nach der Testphase beginnt Dein gewähltes Abonnement automatisch.
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="body1">Laufzeit bis: {formattedEndDate}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Nächste Kontingent-Aktualisierung: {formattedNextRefresh}
                                </Typography>
                                {subscriptionInfo?.cancelAtPeriodEnd && (
                                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                        Dein Abonnement wird am Ende der Laufzeit gekündigt und nicht automatisch verlängert.
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Hier kannst Du Dein Abonnement zum nächstmöglichen Zeitpunkt kündigen. Die Kündigung wird am Ende der aktuellen Vertragslaufzeit wirksam.
                        </Typography>

                        <Button
                            variant="contained"
                            startIcon={<PaymentIcon />}
                            onClick={handleOpenBillingPortal}
                            disabled={portalLoading}
                            fullWidth
                            sx={{ py: 1.2 }}
                        >
                            Abonnement verwalten
                        </Button>
                    </Box>
                </SubscriptionOverviewCard>
            </Stack>
        </BaseSettingsPage>
    );
};

export default SubscriptionOverviewPage;
